import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { obtenerObjetoConUsuarioByCode, solicitarDesbloqueo } from "../../services/api";
import nfcAuthService from "../../services/nfcAuthService";
import toast from 'react-hot-toast';

// Light theme color palette
const colors = {
  bg: '#f9fafb',
  surface: '#ffffff',
  surfaceHover: '#f3f4f6',
  border: '#e5e7eb',
  borderFocus: '#84cc16',
  accent: '#84cc16',
  accentHover: '#65a30d',
  textPrimary: '#1f2937',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  error: '#dc2626',
  errorBg: '#fef2f2',
  success: '#16a34a',
  successBg: '#f0fdf4',
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: colors.bg,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem 1rem",
  },
  card: {
    width: "100%",
    maxWidth: "400px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "12px",
    padding: "2rem",
    textAlign: "center",
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    marginBottom: "1.5rem",
  },
  logoIcon: {
    width: "48px",
    height: "48px",
    backgroundColor: colors.accent,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: "0.5rem",
    letterSpacing: "-0.025em",
  },
  subtitle: {
    fontSize: "0.875rem",
    color: colors.textSecondary,
    marginBottom: "1.5rem",
    lineHeight: 1.5,
  },
  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid transparent",
    borderTopColor: colors.accent,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 1rem",
  },
  button: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: colors.accent,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s, transform 0.1s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  outlineButton: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.textPrimary,
    backgroundColor: "transparent",
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  alert: {
    padding: "1rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75rem",
    textAlign: "left",
  },
  alertError: {
    backgroundColor: colors.errorBg,
    border: `1px solid ${colors.error}`,
    color: colors.error,
  },
  alertSuccess: {
    backgroundColor: colors.successBg,
    border: `1px solid ${colors.success}`,
    color: colors.success,
  },
  buttonSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTopColor: '#ffffff',
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },
  lockIcon: {
    width: "64px",
    height: "64px",
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 1.5rem",
  },
};

// Add keyframes for spinner animation
const spinnerKeyframes = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default function ScanPage() {
  const [, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [objeto, setObjeto] = useState(null);
  const [uiState, setUiState] = useState('loading');
  const [solicitandoAcceso, setSolicitandoAcceso] = useState(false);
  const [accesoSolicitado, setAccesoSolicitado] = useState(false);

  const navigate = useNavigate();
  const baseUrl = window.location.origin;

  useEffect(() => {
    const verificarYRedirigir = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (!code) {
        navigate('/');
        return;
      }

      try {
        const result = await obtenerObjetoConUsuarioByCode(code);

        if (result.error) {
          throw new Error(result.error);
        }

        if (!result.objeto) {
          throw new Error("No se encontró un objeto con ese código.");
        }

        setObjeto(result.objeto);

        const requiereActivacion = result.objeto.estado === 'inactivo';

        if (requiereActivacion) {
          navigate('/activacion', { state: { codigoAcceso: code } });
          return;
        }

        if (result.objeto.bloqueado) {
          setUiState('blocked');
          setIsLoading(false);
          return;
        }

        if (result.objeto.estado === 'activo' && result.objeto.ownerId) {
           try {
            const loginResult = await nfcAuthService.loginConCodigoNFC(code);

            if (loginResult.success) {
              const uid = result.objeto.ownerId;
              navigate(`/userpanel?doc=${uid}`);
              return;
            }
          } catch (loginError) {
            // Normal if not the owner
          }
        }

        navigate(`/consultar?registro=${code}`);

      } catch (err) {
        console.error('Error en el flujo de escaneo:', err);
        setError(err.message || "Ocurrió un error inesperado al verificar el código.");
        setUiState('error');
        setIsLoading(false);
      }
    };

    verificarYRedirigir();
  }, [navigate]);

  if (uiState === 'loading') {
    return (
      <div style={styles.container}>
        <style>{spinnerKeyframes}</style>
        <div style={styles.main}>
          <div style={styles.card}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
            </div>
            <div style={styles.spinner} />
            <h1 style={styles.title}>Verificando código...</h1>
            <p style={styles.subtitle}>Por favor espera un momento</p>
          </div>
        </div>
      </div>
    );
  }

  if (uiState === 'error') {
    return (
      <div style={styles.container}>
        <style>{spinnerKeyframes}</style>
        <div style={styles.main}>
          <div style={styles.card}>
            <div style={styles.lockIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={colors.error} strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
            </div>
            <h1 style={{...styles.title, color: colors.error}}>Error</h1>
            <p style={styles.subtitle}>{error}</p>
            <button
              onClick={() => navigate('/')}
              style={styles.outlineButton}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (uiState === 'blocked' && objeto) {
    return (
      <div style={styles.container}>
        <style>{spinnerKeyframes}</style>
        <div style={styles.main}>
          <div style={styles.card}>
            <div style={styles.lockIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={colors.error} strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h1 style={styles.title}>Panel Bloqueado</h1>
            <p style={styles.subtitle}>
              Para proteger la información, el propietario ha restringido el acceso a este panel.
            </p>

            <button
              onClick={async () => {
                setSolicitandoAcceso(true);
                try {
                  await solicitarDesbloqueo(objeto.id, baseUrl);
                  toast.success("Solicitud enviada al propietario");
                  setAccesoSolicitado(true);
                } catch (desbloqueoError) {
                  toast.error("Error: " + desbloqueoError.message);
                } finally {
                  setSolicitandoAcceso(false);
                }
              }}
              disabled={solicitandoAcceso || accesoSolicitado}
              style={{
                ...styles.button,
                ...((solicitandoAcceso || accesoSolicitado) ? styles.buttonDisabled : {}),
                marginBottom: accesoSolicitado ? "1rem" : 0,
              }}
            >
              {solicitandoAcceso && <div style={styles.buttonSpinner} />}
              {solicitandoAcceso ? 'Enviando...' :
               accesoSolicitado ? 'Acceso Solicitado' :
               'Solicitar Acceso al Propietario'}
            </button>

            {accesoSolicitado && (
              <div style={{...styles.alert, ...styles.alertSuccess}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>
                  <strong>¡Listo!</strong> El propietario recibirá tu solicitud por correo electrónico.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
